import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import GoogleIcon from '../components/GoogleIcon';
import GooglePlayBadge, { PLAY_TRADEMARK_NOTICE } from '../components/GooglePlayBadge';
import ScreenshotCarousel from '../components/ScreenshotCarousel';
import { startGoogleSignIn } from '../services/googleSignIn';

const isNative = Capacitor.isNativePlatform();

const STEPS = [
  {
    title: 'Enter your zipcode',
    text: isNative
      ? 'Sign in with Google and tell us where you hunt. Scoring automatically adjusts to how deer behave in your area.'
      : 'Get the app on Google Play, sign in with Google and tell us where you hunt. Scoring automatically adjusts to how deer behave in your area.',
  },
  {
    title: 'We score every hunt',
    text: 'Weather, wind, temperature, fronts, pressure and the moon are combined into a 1–5 star rating for each morning and evening.',
  },
  {
    title: 'Pick your best days',
    text: 'See up to 7 days with Plus, drill into any forecast day hour by hour, or scan the monthly calendar to plan ahead.',
  },
];

const FEATURES = [
  { icon: '⭐', title: '7-Day Outlook', text: 'Morning and evening ratings with a plain-English summary of why. Free includes 3 days; Plus includes all 7.' },
  { icon: '🕒', title: 'Hourly Daily View', text: 'Charts showing how conditions change from before sunrise through dark.' },
  { icon: '📅', title: 'Monthly Calendar', text: 'Plan the whole month with moon-based ratings, upgraded with weather as days approach.' },
  { icon: '🌙', title: 'Lunar Feeding', text: 'Moon overhead and underfoot timing measured against dawn and dusk.' },
];

function LandingPage({ testerOptIn = false }: { testerOptIn?: boolean }) {
  const [error, setError] = useState('');
  const testerLink = (
    <a
      href="https://groups.google.com/g/standlogic-app-testers"
      className="btn btn-primary btn-lg"
      target="_blank"
      rel="noopener noreferrer"
    >
      Become a StandLogic Tester
    </a>
  );

  const handleStart = async () => {
    setError('');
    try {
      await startGoogleSignIn();
    } catch (err) {
      console.error('Unable to open Google sign-in:', err);
      setError('Unable to start Google sign-in. Please try again.');
    }
  };

  return (
    <div className="landing">
      <section className="landing-hero">
        <img src="/favicon.png?v=2" alt="" className="landing-logo" />
        <h2>Know when conditions favor a whitetail hunt.</h2>
        <p className="landing-lead">
          Built for whitetail deer hunters, StandLogic turns weather, wind, pressure and moon conditions into clear
          1–5 star morning and evening forecasts—giving you the essentials to plan your time in the stand,
          without paying for features you don’t need.
        </p>

        {error && <div className="alert alert-error" role="alert">{error}</div>}

        {testerOptIn ? (
          <>
            <div className="landing-cta">{testerLink}</div>
            <p className="auth-note">
              Join the StandLogic testers Google Group to become a tester.
            </p>
          </>
        ) : isNative ? (
          <>
            <div className="landing-cta">
              <button type="button" className="btn btn-google btn-lg" onClick={handleStart}>
                <GoogleIcon />
                Get Started for Free. Sign In Today.
              </button>
            </div>
            <p className="auth-note">
              New here? One tap creates your account with <Link to="/support" state={{ tab: 'google-auth' }}>Google</Link>.
              Already a member? Use the same button to sign in.
            </p>
          </>
        ) : (
          <>
            <div className="landing-cta">
              <GooglePlayBadge />
            </div>
            <p className="auth-note">
              Free to download for Android. Sign in with your <Link to="/support" state={{ tab: 'google-auth' }}>Google</Link> account
              in the app to get started.
            </p>
          </>
        )}
      </section>

      {!isNative && (
        <>
          <img
            className="landing-feature-image"
            src="/screenshots/feature-photo.png"
            alt="StandLogic: Forecast deer movement. A simple score for your next morning or evening hunt."
            width={1795}
            height={876}
            loading="lazy"
            decoding="async"
          />
          <ScreenshotCarousel />
        </>
      )}

      <section className="landing-section" aria-labelledby="how-it-works">
        <h3 id="how-it-works" className="landing-heading">How it works</h3>
        <ol className="landing-steps">
          {STEPS.map((step, i) => (
            <li key={step.title} className="landing-step">
              <span className="landing-step-number">{i + 1}</span>
              <h4>{step.title}</h4>
              <p>{testerOptIn && i === 0
                ? 'Join the testers Google Group, then sign in to the app with Google and tell us where you hunt. Scoring automatically adjusts to how deer behave in your area.'
                : step.text}</p>
            </li>
          ))}
        </ol>
      </section>

      <section className="landing-section" aria-labelledby="what-you-get">
        <h3 id="what-you-get" className="landing-heading">What you get</h3>
        <div className="landing-features">
          {FEATURES.map((feature) => (
            <div key={feature.title} className="landing-feature">
              <span className="landing-feature-icon" aria-hidden="true">{feature.icon}</span>
              <h4>{feature.title}</h4>
              <p>{feature.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="landing-section" aria-labelledby="free-plus">
        <h3 id="free-plus" className="landing-heading">Free and Plus</h3>
        <div className="landing-plans">
          <div className="landing-plan">
            <h4>Free</h4>
            <ul>
              <li>Weather-based scores for the next 3 days</li>
              <li>Hourly view for the next 3 days</li>
              <li>Full monthly calendar (moon-based beyond 3 days)</li>
            </ul>
          </div>
          <div className="landing-plan landing-plan-plus">
            <h4>Plus</h4>
            <ul>
              <li>Everything in Free</li>
              <li>Full 7-day outlook and hourly view</li>
              <li>Weather in the monthly calendar across the forecast window</li>
              <li>Save up to 5 named hunting locations</li>
            </ul>
          </div>
        </div>
        <p className="auth-note">
          <Link to="/support" state={{ tab: 'free-vs-plus' }}>See the full comparison</Link>
        </p>
      </section>

      <section className="landing-section landing-final">
        <h3 className="landing-heading">Ready for your next hunt?</h3>
        {testerOptIn ? testerLink : isNative ? (
          <button type="button" className="btn btn-google btn-lg" onClick={handleStart}>
            <GoogleIcon />
            Get Started for Free. Sign In Today.
          </button>
        ) : (
          <GooglePlayBadge />
        )}
      </section>

      <footer className="landing-footer">
        <Link to="/privacy">Privacy Policy</Link>
        <Link to="/terms">Terms of Service</Link>
        <Link to="/disclaimer">Disclaimer</Link>
        <Link to="/delete-account">Delete Account</Link>
        <Link to="/support">Support</Link>
      </footer>
      {!isNative && !testerOptIn && <p className="landing-trademark">{PLAY_TRADEMARK_NOTICE}</p>}
    </div>
  );
}

export default LandingPage;
